/* eslint-disable no-plusplus */
import humps from 'humps'
import dateFormat from 'date-fns/format'
import parse from 'date-fns/parse'
import isValid from 'date-fns/isValid'

import {
  PHONE_NUMBER_REGEX,
  EMAIL_REGEX,
  BANK_ACCOUNT_FIELDS,
  BANK_ACCOUNT_TYPE_OPTIONS,
  KEY_PIX_OPTIONS,
} from '_utils/constants'

/** Converte item de array para FormData: mantém arquivo/blob, serializa objeto em JSON e ignora null/undefined. */
const formDataListItemValue = item => {
  if (item === null || item === undefined) return null
  if (item instanceof Blob) return item
  if (typeof item === 'object') return JSON.stringify(item)
  return item
}

/**
 * Monta FormData para endpoints Django com suporte a arrays e arquivos.
 * @param {Object} data
 * @param {boolean} shouldDecamelize
 * @returns {FormData}
 */
export const createFormDataWithFiles = (data, shouldDecamelize = false) => {
  const formData = new FormData()
  Object.keys(data).forEach(field => {
    const formattedField = shouldDecamelize ? humps.decamelize(field) : field
    if (Array.isArray(data[field])) {
      data[field].forEach(item => formData.append(formattedField, item))
    } else if (typeof data[field] === 'object' && data[field] !== null) {
      formData.append(formattedField, JSON.stringify(data[field]))
    } else {
      formData.append(formattedField, data[field])
    }
  })

  return formData
}

export const createFormData = (data, shouldDecamelize = false, options = {}) => {
  const formData = new FormData()
  const { multipartArraysAsJson = false } = options

  Object.keys(data).forEach(field => {
    const fieldValue = data[field]
    const formattedField = shouldDecamelize ? humps.decamelize(field) : field

    // Handle arrays according to endpoint contract (single JSON or repeated field)
    if (Array.isArray(fieldValue)) {
      if (multipartArraysAsJson) {
        formData.append(formattedField, JSON.stringify(fieldValue))
      } else if (fieldValue.length > 0) {
        fieldValue.forEach(item => {
          const formValue = formDataListItemValue(item)
          if (formValue !== null) formData.append(formattedField, formValue)
        })
      }
      return
    }

    // Handle other values
    const formDataValue = (() => {
      if (fieldValue === null || fieldValue === undefined) {
        return ''
      }
      if (fieldValue instanceof Blob || typeof fieldValue !== 'object') {
        return fieldValue
      }
      return JSON.stringify(fieldValue)
    })()

    if (formDataValue !== '') {
      formData.append(formattedField, formDataValue)
    }
  })

  return formData
}

export const createFormDataDocuments = (data, isCamelized = false) => {
  const formData = new FormData()
  Object.keys(data).forEach(field => {
    const fieldValue = data[field]

    const hasFile = Array.isArray(fieldValue)
      ? fieldValue?.find(value => value instanceof Blob)
      : false
    const formDataValue = (() => {
      if (!fieldValue && typeof fieldValue !== 'boolean') {
        return ''
      }
      if (
        fieldValue instanceof Blob ||
        typeof fieldValue !== 'object' ||
        hasFile ||
        Array.isArray(fieldValue)
      ) {
        return fieldValue
      }

      return JSON.stringify(fieldValue)
    })()

    if (Array.isArray(formDataValue)) {
      formDataValue.forEach(value => {
        if (value instanceof Blob && value.constructor === Blob) {
          formData.append(isCamelized ? field : humps.decamelize(field), value, value.name)
        } else {
          formData.append(isCamelized ? field : humps.decamelize(field), value)
        }
      })
    } else if (formDataValue instanceof Blob && formDataValue.constructor === Blob) {
      formData.append(
        isCamelized ? field : humps.decamelize(field),
        formDataValue,
        formDataValue.name
      )
    } else {
      formData.append(isCamelized ? field : humps.decamelize(field), formDataValue)
    }
  })

  return formData
}

export const formatFormData = (data, isCamelized = false) => {
  const formData = new FormData()
  Object.keys(data).forEach(field => {
    const fieldValue = data[field]

    if (fieldValue === null || fieldValue === undefined) {
      return
    }

    const hasFile = Array.isArray(fieldValue)
      ? fieldValue?.find(value => value instanceof Blob)
      : false
    const formDataValue = (() => {
      if (
        fieldValue instanceof Blob ||
        typeof fieldValue !== 'object' ||
        hasFile ||
        Array.isArray(fieldValue)
      ) {
        return fieldValue
      }

      // Convertendo todos os outros tipos para string
      return String(fieldValue)
    })()

    if (Array.isArray(formDataValue)) {
      formDataValue.forEach(value => {
        if (value instanceof Blob && value.constructor === Blob) {
          formData.append(isCamelized ? field : humps.decamelize(field), value, value.name)
        } else {
          formData.append(isCamelized ? field : humps.decamelize(field), value)
        }
      })
    } else if (formDataValue instanceof Blob && formDataValue.constructor === Blob) {
      formData.append(
        isCamelized ? field : humps.decamelize(field),
        formDataValue,
        formDataValue.name
      )
    } else {
      formData.append(isCamelized ? field : humps.decamelize(field), formDataValue)
    }
  })

  return formData
}

export const stringifyBoolean = value => (typeof value === 'boolean' ? value.toString() : 'false')

export const stringifyNumber = value => (typeof value === 'number' ? value.toString() : '0')

export const parseToNumber = string => (string ? string.replace(/[^+0-9]+/g, '') : '')

export const validationPhoneNumber = phone => {
  const phoneNumber = parseToNumber(phone)
  if (!!phoneNumber && phoneNumber.length === 0) {
    return false
  }
  return !PHONE_NUMBER_REGEX.test(phoneNumber)
}

export const validationEmail = email => {
  if (!email) {
    return false
  }

  return EMAIL_REGEX.test(email)
}

export const validationCep = cep => {
  const validationCepRegex = /^[0-9]{8}$/
  return !validationCepRegex.test(parseToNumber(cep))
}

// https://gist.github.com/alexbruno/6623b5afa847f891de9cb6f704d86d02
export const validationCNPJ = value => {
  if (!value) return false

  // Accept string or number
  const validTypes = typeof value === 'string' || Number.isInteger(value)

  // Remove invalid options
  if (!validTypes) return false

  // Array with all number
  const numbers = value.toString().match(/\d/g).map(Number)

  // Valid numbers of digits
  if (numbers.length !== 14) return false

  // Remove values with only equals number
  const items = [...new Set(numbers)]
  if (items.length === 1) return false

  // Validation
  const calc = x => {
    const slice = numbers.slice(0, x)
    let factor = x - 7
    let sum = 0

    for (let i = x; i >= 1; i--) {
      const n = slice[x - i]
      sum += n * factor--
      if (factor < 2) factor = 9
    }

    const result = 11 - (sum % 11)

    return result > 9 ? 0 : result
  }

  // Separate the last 2 numbers
  const digits = numbers.slice(12)

  // Validate first number
  const digit0 = calc(12)
  if (digit0 !== digits[0]) return false

  // Validate second number
  const digit1 = calc(13)
  return digit1 === digits[1]
}

export const validationCPF = inputCPF => {
  const cpf = parseToNumber(inputCPF)
  let sum = 0
  let rest

  if (/^(\d)\1{10}$/.test(cpf)) {
    return false
  }

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i), 10) * (11 - i)
  }

  rest = (sum * 10) % 11

  if (rest === 10 || rest === 11) {
    rest = 0
  }

  if (rest !== parseInt(cpf.substring(9, 10), 10)) {
    return false
  }

  sum = 0
  // eslint-disable-next-line no-plusplus
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i), 10) * (12 - i)
  }

  rest = (sum * 10) % 11

  if (rest === 10 || rest === 11) {
    rest = 0
  }

  if (rest !== parseInt(cpf.substring(10, 11), 10)) {
    return false
  }

  return true
}

export const cpfCnpjMask = value => {
  const number = parseToNumber(value)
  if (number.length === 14) {
    return number.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5')
  }
  if (number.length === 11) {
    return number.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, '$1.$2.$3-$4')
  }

  return number
}

export const notEmptyPayload = payload => {
  const undefinedValues = [undefined, null, [], '', 0]
  const newPayload = Object.keys(payload).reduce(
    (acc, key) =>
      undefinedValues.includes(payload[key]) ||
      (Array.isArray(payload[key]) && payload[key].length === 0)
        ? acc
        : { ...acc, [key]: payload[key] },
    {}
  )
  return newPayload
}

const toTitleCase = phrase =>
  phrase
    ? phrase
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : ''

export const getPropertyAddress = address => `${address ? toTitleCase(address) : ''}`

export const getPage = query =>
  query && (query.includes('?page=') || query.includes('&page='))
    ? Number(query.match(/[?&]page=([^&#]*)/)[1])
    : undefined

export const validationDate = date => {
  return date?.replace(/[^0-9]+/g, '').length && isValid(parse(date, 'dd/MM/yyyy', new Date()))
}

export const parseNumber = value => (value ? value.replace(/[^0-9]+/g, '') : '')

/**
 * @param {string} phoneNumber - It can be `8-14+` number, formatted or not, as it's going to be parsed anyway
 */
export function generateWhatsappLink(phoneNumber) {
  const parsedNumber = parseWhatsappNumber(phoneNumber)
  return `https://api.whatsapp.com/send?phone=${parsedNumber}`
}

/**
 * How this method converts the number:
 *
 * Based on 8-14+ rules (see `formatPhoneValueRules`)
 * - 8: XXXX-XXXX -> 55XXXXXXXX
 * - 9: YXXXX-XXXX -> 55YXXXXXXXX
 * - 10: (DD) XXXX-XXXX -> 55DDXXXXXXXX
 * - 11: (DD) YXXXX-XXXX -> 55DDYXXXXXXXX
 * - 12: keeps as is
 * - 13: +CC (DD) YXXXX-XXXX -> CCDDYXXXXXXXX
 * - 14+: keeps as is
 *
 * Where:
 * - C: Country Code
 * - D: DDD
 * - X: Phone Number
 * - Y: 9th number (only in Brazil)
 */
export function parseWhatsappNumber(phoneNumber) {
  const number = parseNumber(phoneNumber)
  if ([8, 9, 10, 11].includes(number.length)) {
    return `55${number}`
  }
  if ([12, 13, 14].includes(number.length)) {
    return number
  }
  throw new Error('Invalid phone number length')
}

export const phoneNumberMask = value => {
  const number = parseNumber(value)
  if (number.length === 13) {
    return number.replace(/^(\d{2})(\d{2})(\d{1})(\d{4})(\d{4}).*/, '+$1 ($2) $3 $4-$5')
  }
  if (number.length === 12) {
    return number.replace(/^(\d{2})(\d{2})(\d{4})(\d{4}).*/, '+$1 ($2) $3-$4')
  }
  if (number.length === 11) {
    return number.replace(/^(\d{2})(\d{1})(\d{4})(\d{4}).*/, '+55 ($1) $2 $3-$4')
  }
  if (number.length === 10) {
    return number.replace(/^(\d{2})(\d{4})(\d{4}).*/, '($1) $2-$3')
  }

  if (number.length === 8) {
    return number.replace(/^(\d{2})(\d{4})(\d{4}).*/, '$3-$4')
  }

  return number
}

export function numbersValid(value) {
  const hasElevenDigitsAndNineAfterDDD =
    value && value?.replace(/[^\d]/g, '').length === 11 && value?.replace(/[^\d]/g, '')[2] === '9'
  const hasThirteenDigitsAndNineAfterDDD =
    value && value?.replace(/[^\d]/g, '').length === 13 && value?.replace(/[^\d]/g, '')[4] === '9'
  return hasElevenDigitsAndNineAfterDDD || hasThirteenDigitsAndNineAfterDDD
}

export function formatPhoneValueMask(value) {
  const digits = value?.replace(/[^\d]/g, '')

  if (digits.length === 0) {
    return ''
  }

  if (digits?.length <= 5) {
    return `${digits?.slice(0, 5)}`
  }

  if (digits?.length <= 8) {
    return `${digits?.slice(0, 4)}-${digits?.slice(4, 8)}`
  }

  if (digits?.length <= 10) {
    return `(${digits?.slice(0, 2)}) ${digits?.slice(2, 6)}-${digits?.slice(6, 10)}`
  }

  if (digits?.length <= 11) {
    return `(${digits?.slice(0, 2)}) ${digits?.slice(2, 3)} ${digits?.slice(3, 7)}-${digits?.slice(
      7,
      11
    )}`
  }

  return `+${digits?.slice(0, 2)} (${digits?.slice(2, 4)}) ${digits?.slice(4, 5)} ${digits?.slice(
    5,
    9
  )}-${digits?.slice(9, 13)}`
}

/**
 * Format the phone value to be used in the input mask
 *
 * @return {string}
 * @param {string} value - The phone value to be formatted.
 * @param {'8-14+'} formats - The rules to be applied to the phone value.
 *
 * `8-14+`
 * - 8: XXXX-XXXX
 * - 9: XXXXX-XXXX
 * - 10: (XX) XXXX-XXXX
 * - 11: (XX) XXXXX-XXXX
 * - 12: formatted as no mask
 * - 13: +XX (XX) XXXXX-XXXX
 * - 14+: formatted as no mask
 */
export function formatPhoneValueRules(value, formats = '8-14+') {
  const digits = parseNumber(value)

  if (formats === '8-14+') {
    if (digits?.length === 8) {
      return `${digits?.slice(0, 4)}-${digits?.slice(4, 8)}`
    }
    if (digits?.length === 9) {
      return `${digits?.slice(0, 5)}-${digits?.slice(5, 9)}`
    }
    if (digits?.length === 10) {
      return `(${digits?.slice(0, 2)}) ${digits?.slice(2, 6)}-${digits?.slice(6, 10)}`
    }
    if (digits?.length === 11) {
      return `(${digits?.slice(0, 2)}) ${digits?.slice(2, 7)}-${digits?.slice(7, 11)}`
    }
    if (digits?.length === 13) {
      const countryDdd = `+${digits?.slice(0, 2)} (${digits?.slice(2, 4)})`
      return `${countryDdd} ${digits?.slice(4, 9)}-${digits?.slice(9, 13)}`
    }
    if (digits?.length === 12 || digits?.length >= 14) {
      return digits
    }
    throw new Error('Invalid phone number length')
  }
  throw new Error('Invalid format')
}

export const phoneNumberMaskServiceOrder = value => {
  const number = parseNumber(value)
  if (number.length === 13) {
    return number.replace(/^(\d{2})(\d{2})(\d{1})(\d{4})(\d{4}).*/, '($2) $3 $4-$5')
  }
  if (number.length === 12) {
    return number.replace(/^(\d{2})(\d{2})(\d{4})(\d{4}).*/, '($2) $3-$4')
  }
  return number
}

export const formatDate = (date, separator = 'às') => {
  let time = date

  if (typeof date !== 'object') {
    // IF DATE COMES AS A STRING, IT'S CONVERTED INTERNALLY BY THIS FUNCTION
    time = new Date(date)
  }

  const sep = separator ? ` ${separator} ` : ' '

  return dateFormat(time, `dd/MM/yyyy'${sep}'HH:mm`)
}

export const toSlug = str => {
  const strTrim = str.replace(/^\s+|\s+$/g, '') // trim
  const strLowerCase = strTrim.toLowerCase()

  // remove accents, swap ñ for n, etc
  const from = 'àáäâãāèéëêìíïîòóöôõùúüûñç·/-,:;'
  const to = 'aaaaaaeeeeiiiiooooouuuunc______'
  // eslint-disable-next-line
  for (let i = 0, l = from.length; i < l; i++) {
    strLowerCase.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i))
  }

  strLowerCase
    .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '_') // collapse whitespace and replace by -
    .replace(/_+/g, '_') // collapse dashes
    .replace(/_+/g, '_')

  return strLowerCase
}

export const formatInputNumber = (e, min, max) => {
  if (!e.target.value.match(/^[0-9]+$/)) {
    e.target.value = null
  }

  if (Number(e.target.value) < min) {
    e.target.value = min
  }

  if (Number(e.target.value) > max) {
    e.target.value = max
  }

  e.target.value = parseInt(e.target.value, 10) || 0

  return e.target.value
}

export const formatAmount = amount => {
  const current = Number(amount).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
  return current.replace('R$', '')
}

export const formatDateDays = (date, initialState = 'yyyy-MM-dd', formatStr = 'dd/MM/yyyy') => {
  const currentDate = typeof date === 'string' ? parse(date, initialState, new Date()) : date
  if (isValid(currentDate)) {
    return dateFormat(currentDate, formatStr)
  }
  return null
}

// Format hh:mm:ss to Date
export const formatTimeToDate = time => {
  const [hours, minutes, seconds] = time.split(':')

  return new Date(null, null, null, hours, minutes, seconds)
}

export const reducerBankAccount = values => {
  const newValues = Object.entries(values).reduce((acc, [key, value]) => {
    if (key === BANK_ACCOUNT_FIELDS.BANK && value !== null && value !== undefined) {
      return {
        ...acc,
        [key]: value.id,
      }
    }
    if (key === BANK_ACCOUNT_FIELDS.BANK_ACCOUNT_TYPE && value !== null && value !== undefined) {
      return {
        ...acc,
        [key]: BANK_ACCOUNT_TYPE_OPTIONS.find(item => item.value === value)?.value || '',
      }
    }
    if (key === BANK_ACCOUNT_FIELDS.PIX_KEY_CHOICE && value !== null && value !== undefined) {
      return {
        ...acc,
        [key]: KEY_PIX_OPTIONS.find(item => item.value === value)?.value || '',
      }
    }
    if (value === null || value === undefined) {
      return { ...acc, [key]: '' }
    }
    return { ...acc, [key]: value }
  }, {})
  return newValues
}

export const removeDuplicatedObjectFromArray = (array, key) => {
  return array.filter((value, index, self) => index === self.findIndex(t => t[key] === value[key]))
}

export const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)

export const convertCurrencyValueToNumber = value => {
  const stringifiedValue = String(value).replace('R$', '')

  if (stringifiedValue.length > 6) {
    return Number(stringifiedValue.replace(/\./g, '').replace(',', '')) / 100
  }

  if (stringifiedValue) {
    return Number(stringifiedValue.replace(',', '.'))
  }

  return null
}

export const formatAddress = (
  property,
  useLineBreak = false,
  useCep = false,
  useComplement = false
) => {
  const allKeys = ['number', 'complement', 'cep', 'neighborhood', 'city', 'uf']
  const conditionalKeys = ['complement', 'cep']
  const address = property.get('address')

  const filteredKeys = allKeys.reduce((acc, currentKey) => {
    if (!conditionalKeys.includes(currentKey)) {
      return [...acc, currentKey]
    }

    const showComplement = useComplement && property.get('complement')
    const showCEP = useCep && property.get('cep')

    if ((currentKey === 'cep' && showCEP) || (currentKey === 'complement' && showComplement)) {
      return [...acc, currentKey]
    }
    return acc
  }, [])

  const fullAddress = filteredKeys.reduce((acc, key) => {
    if (!property.get(key)) {
      return acc
    }

    if (key === 'uf') {
      return `${acc}-${property.get(key)}`
    }

    const lineBreak =
      useLineBreak && (key === 'complement' || (key === 'neighborhood' && !useComplement))

    return lineBreak ? `${acc}\n${property.get(key)}` : `${acc}, ${property.get(key)}`
  }, `${address}`)

  return fullAddress
}

export const generateUniqueKey = value => {
  let id = ''

  try {
    id = crypto.randomUUID()
  } catch (e) {
    // crypto.randomUUID() not supported
    id = value + Date.now()
  }

  return id
}

export function formatMoney(number) {
  const value = number || 0
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export const displayCurrency = (value, defaultValue = 'R$ 0,00') => {
  return value ? `R$ ${formatMoney(value)}` : defaultValue
}

/**
 * @description Reorders a list moving an element of it to another position
 * @param {any[]} list An array of anything that will reordered
 * @param {number} startIndex The element's index that should be moved
 * @param {number} endIndex The index where the element should be moved
 */
export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

export const validatePasswordStrength = password => {
  if (password.length < 8 || !password) {
    return false
  }

  let hasDigit = false
  let hasLowercase = false
  let hasSpecialChar = false
  const specialChars = '!@#$%^&*()_+-='

  for (let i = 0; i < password.length; i += 1) {
    const char = password[i]
    if (!hasDigit && !isNaN(char)) {
      hasDigit = true
    }
    if (!hasLowercase && char === char.toLowerCase() && isNaN(char)) {
      hasLowercase = true
    }
    if (!hasSpecialChar && specialChars.includes(char)) {
      hasSpecialChar = true
    }
  }

  return hasDigit && hasLowercase && hasSpecialChar
}

export function handleDatagridOrderBy({ order, defaultOrder }) {
  const orderObj = order?.[0]
  const { field = defaultOrder, sort = 'asc' } = orderObj || {}

  const decamelizedField = humps.decamelize(field)
  return sort === 'desc' ? `-${decamelizedField}` : decamelizedField
}

export const getAgencyTerms = ({ serviceOrder, agency }) => {
  const source = agency || serviceOrder?.agency
  const isImmutable = typeof source?.get === 'function'
  const getValue = key => {
    if (!source) return ''
    return isImmutable ? source.get(key) : source?.[key]
  }
  const owner = getValue('ownerTerm')
  const tenant = getValue('tenantTerm')
  const normalizedOwner = typeof owner === 'string' ? owner.trim() : ''
  const normalizedTenant = typeof tenant === 'string' ? tenant.trim() : ''
  const ownerTerm = normalizedOwner || 'Proprietário'
  const tenantTerm = normalizedTenant || 'Solicitante'
  const isAgencyLoaded = Boolean(source && (isImmutable ? source.get('id') : source?.id))
  return { ownerTerm, tenantTerm, isAgencyLoaded }
}

export const isAgencySaas = agency => {
  if (!agency) return false
  const isImmutable = typeof agency?.get === 'function'
  const contractType = isImmutable ? agency.get('contractType') : agency?.contractType
  return contractType === 'SAAS'
}

export const mergeAiSuggestions = ({
  existingAiSuggestions = {},
  correctionResponse,
  spellingResponse,
}) => {
  return {
    price_conference: existingAiSuggestions?.priceConference ?? [],

    correct_and_improve: correctionResponse
      ? [correctionResponse]
      : existingAiSuggestions?.correctAndImprove ?? [],

    spelling_correction: spellingResponse
      ? [spellingResponse]
      : existingAiSuggestions?.spellingCorrection ?? [],
  }
}

export const getMaterialsInput = materials =>
  materials.reduce(
    (acc, m, index) => {
      const desc = (m?.description || '').trim()
      if (!desc) return acc

      acc.mapped.push(index)
      acc.input[index] = desc
      return acc
    },
    { mapped: [], input: {} }
  )

export const separateAttachmentsByType = (attachments, isFilePDFFn) => {
  const safeAttachments = Array.isArray(attachments) ? attachments : []
  if (typeof isFilePDFFn !== 'function') {
    return { media: safeAttachments, pdf: [] }
  }
  return {
    media: safeAttachments.filter(file => !isFilePDFFn(file)),
    pdf: safeAttachments.filter(file => isFilePDFFn(file)),
  }
}
